import { Box, Card, Group, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import type { ReactNode } from 'react';

/** A single label/value pair rendered in the {@link MobileListCard} stat row. */
export interface MobileListStat {
  /** Short metric label (e.g. "Qty", "Value", "Cap"). Rendered dimmed above the value. */
  label: string;
  /** Metric value. Any renderable node (string, number, formatted JSX). */
  value: ReactNode;
  /** Optional colour applied to the value text (Mantine colour token or CSS colour). */
  color?: string;
}

/** Props for {@link MobileListCard}. */
export interface MobileListCardProps {
  /** Primary line — the row's name / title. Clamped to one line. */
  title: ReactNode;
  /** Optional dim secondary line below the title (e.g. description, set name). Clamped to one line. */
  subtitle?: ReactNode;
  /**
   * Optional leading visual rendered left of the text column — a tinted icon
   * tile, colour dot, thumbnail, or binder cover. Should be ≤44px square.
   */
  leading?: ReactNode;
  /**
   * Optional badge / chip row rendered under the subtitle (e.g. type badge,
   * "Kiosk" pill, tag chips). Wraps on overflow.
   */
  badges?: ReactNode;
  /**
   * Optional compact stat row — 2-4 label/value pairs (count, value, capacity…).
   * Rendered as evenly-spaced columns under the badges.
   */
  stats?: MobileListStat[];
  /**
   * Optional right-aligned meta node next to the chevron (e.g. "3d ago",
   * a price). Kept visually quiet so it doesn't compete with the title.
   */
  meta?: ReactNode;
  /**
   * Optional trailing action rendered to the FAR right, OUTSIDE the tappable
   * surface (e.g. a kebab `<Menu>` / `<ActionIcon>`). Kept out of the button so
   * we never nest interactive controls. When omitted and `onClick` is set, a
   * chevron affordance is shown instead.
   */
  action?: ReactNode;
  /** Tap handler for the whole card surface (typically navigate-to-detail). */
  onClick?: () => void;
  /** Accessible label for the tappable surface. Falls back to `title` when it is a string. */
  ariaLabel?: string;
  /** Forwarded to the outer Card for E2E / query hooks. */
  'data-testid'?: string;
  /**
   * Optional selection state. When defined the card shows a subtle brand-tinted
   * background; pair with an `action` checkbox to drive it.
   */
  selected?: boolean;
}

/**
 * MobileListCard — a tappable, stacked-card row used to replace wide data
 * tables on phone-width management pages (Phase 3C-1).
 *
 * Shell: (desktop) management pages, phone breakpoint only. Pages render their
 * existing `<Table>` at ≥md and map the same rows to `MobileListCard`s below md
 * (gated on `useIsPhone()` so only one tree mounts and desktop is untouched).
 *
 * Anatomy (all slots optional except `title`):
 *
 *   ┌──────────────────────────────────────────────┐
 *   │ [leading]  Title …………………………………  [meta] [›/action] │
 *   │            Subtitle ……………………………                  │
 *   │            [badge] [badge] …                      │
 *   │            Label   Label   Label                 │
 *   │            Value   Value   Value                 │
 *   └──────────────────────────────────────────────┘
 *
 * The card body (everything except `action`) is one `<UnstyledButton>` so the
 * whole surface is keyboard- and screen-reader-focusable with a single tap
 * target ≥44px tall. The trailing `action` (e.g. a kebab menu / checkbox) is
 * rendered as a sibling OUTSIDE that button so interactive controls never nest.
 *
 * Visual language matches the app DS: `<Card withBorder radius="md">`, dimmed
 * `xs` labels, tabular-nums stat values, brand-tinted selection background.
 * Stat labels render as MTB eyebrows — mono, uppercase, `0.18em` tracking —
 * rather than the ad-hoc 10px/0.3px sans they used before #107.
 */
export function MobileListCard({
  title,
  subtitle,
  leading,
  badges,
  stats,
  meta,
  action,
  onClick,
  ariaLabel,
  selected = false,
  'data-testid': testId,
}: MobileListCardProps) {
  const tappable = typeof onClick === 'function';
  const label = ariaLabel ?? (typeof title === 'string' ? title : undefined);

  const body = (
    <Group gap="sm" wrap="nowrap" align="flex-start" style={{ width: '100%' }}>
      {leading != null && <Box style={{ flexShrink: 0, marginTop: 2 }}>{leading}</Box>}

      <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
        <Group gap="xs" wrap="nowrap" align="flex-start" justify="space-between">
          <Text fw={600} lineClamp={1} style={{ flex: 1, minWidth: 0 }}>
            {title}
          </Text>
          <Group gap={4} wrap="nowrap" align="center" style={{ flexShrink: 0 }}>
            {meta != null && (
              <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                {meta}
              </Text>
            )}
            {tappable && action == null && (
              <IconChevronRight
                size={16}
                stroke={1.5}
                style={{ color: 'var(--mantine-color-dimmed)' }}
                aria-hidden
              />
            )}
          </Group>
        </Group>

        {subtitle != null && (
          <Text size="xs" c="dimmed" lineClamp={1}>
            {subtitle}
          </Text>
        )}

        {badges != null && (
          <Group gap={6} wrap="wrap">
            {badges}
          </Group>
        )}

        {stats != null && stats.length > 0 && (
          <Group gap="lg" wrap="wrap" mt={2}>
            {stats.map((s, i) => (
              <Stack key={`${s.label}-${i}`} gap={0} style={{ minWidth: 0 }}>
                <Text
                  c="dimmed"
                  tt="uppercase"
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 'var(--text-xs)',
                    letterSpacing: 'var(--tracking-eyebrow)',
                    fontWeight: 'var(--weight-semibold)',
                  }}
                >
                  {s.label}
                </Text>
                <Text
                  size="sm"
                  fw={600}
                  style={{ color: s.color, fontVariantNumeric: 'tabular-nums' }}
                >
                  {s.value}
                </Text>
              </Stack>
            ))}
          </Group>
        )}
      </Stack>
    </Group>
  );

  return (
    <Card
      withBorder
      radius="md"
      padding="sm"
      data-testid={testId}
      style={{
        background: selected ? 'var(--mantine-primary-color-light)' : 'var(--mantine-color-body)',
      }}
    >
      <Group gap={4} wrap="nowrap" align="flex-start">
        {tappable ? (
          <UnstyledButton
            onClick={onClick}
            aria-label={label}
            style={{ flex: 1, minWidth: 0, minHeight: 44, display: 'flex', alignItems: 'center' }}
          >
            {body}
          </UnstyledButton>
        ) : (
          <Box
            style={{ flex: 1, minWidth: 0, minHeight: 44, display: 'flex', alignItems: 'center' }}
          >
            {body}
          </Box>
        )}

        {action != null && <Box style={{ flexShrink: 0, alignSelf: 'center' }}>{action}</Box>}
      </Group>
    </Card>
  );
}
